/* global gdAlertify */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import GdTable from './components/GdTable';
import { formatDate, formatSectorLabel } from '../utils/utils';

const VisitasPacienteCoordinacion = ({ modal = false, onClose } = {}) => {
    const navigate = useNavigate();
    const [pacientes, setPacientes] = useState([]);
    const [sectorFilter, setSectorFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [mesFilter, setMesFilter] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        cargarPacientes();
    }, []);

    const cargarPacientes = async () => {
        gdAlertify.showLoader();
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/paciente/visitas-coordinacion');
            setPacientes(data.data || []);
        } catch (error) {
            console.error('Error al cargar pacientes:', error);
            gdAlertify.notiError('Error al cargar pacientes');
        } finally {
            gdAlertify.hideLoader();
            setLoading(false);
        }
    };

    const thisMonth = new Date().toISOString().slice(0, 7);

    const isThisMonth = (raw) => raw && raw.startsWith(thisMonth);

    const sectores = [...new Set(pacientes.map(p => p.sector).filter(Boolean))].sort();

    const filtered = pacientes.filter(p => {
        if (sectorFilter && String(p.sector) !== String(sectorFilter)) return false;
        if (searchFilter) {
            const q = searchFilter.toLowerCase();
            const texto = `${p.nombres} ${p.apellidoPaterno} ${p.apellidoMaterno} ${p.rut}`.toLowerCase();
            if (!texto.includes(q)) return false;
        }
        if (mesFilter) {
            if (!p.ultima_visita_raw || !p.ultima_visita_raw.startsWith(mesFilter)) return false;
        }
        return true;
    });

    const columns = [
        {
            title: 'RUT',
            name: 'rut',
        },
        {
            title: 'Nombre',
            name: 'nombres',
            render: (row) => `${row.nombres} ${row.apellidoPaterno} ${row.apellidoMaterno}`,
        },
        {
            title: 'Sector',
            name: 'sector',
            render: (row) => formatSectorLabel(row.sector),
        },
        {
            title: 'Dirección',
            name: 'direccion',
        },
        {
            title: 'Teléfono',
            name: 'celular',
            render: (row) => row.celular || '—',
        },
        {
            title: 'Observación visita',
            name: 'observacion_visita',
            render: (row) => (
                <span style={{ whiteSpace: 'pre-wrap' }}>{row.observacion_visita || '—'}</span>
            ),
        },
        {
            title: 'Última Visita',
            name: 'ultima_visita_raw',
            render: (row) => (
                Number(row.requiere_visita) === 0 ? (
                    <span className="badge bg-warning text-dark">Paciente solicita no visita</span>
                ) : (
                    <span className={`badge ${isThisMonth(row.ultima_visita_raw) ? 'bg-success' : 'bg-danger'}`}>
                        {row.ultima_visita_raw ? formatDate(row.ultima_visita_raw) : 'Sin registros'}
                    </span>
                )
            ),
        },
        {
            title: 'Acción',
            name: 'accion',
            render: (row) => (
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate(`/form-visita?pacienteID=${row.id}`)}
                >
                    Registrar Visita
                </button>
            ),
        },
    ];

    const contenido = (
        <>
            <div className={modal ? "modal-header" : "d-flex justify-content-between align-items-center mb-4"}>
                <h3 className="mb-0">Visitas Paciente</h3>
                {modal && <button type="button" className="btn-close" onClick={onClose}></button>}
            </div>
            <div className={modal ? "modal-body overflow-auto" : ""}>
                <div className="border rounded p-3 mb-4">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Sector</label>
                            <select
                                className="form-select"
                                value={sectorFilter}
                                onChange={e => setSectorFilter(e.target.value)}
                            >
                                <option value="">Todos los sectores</option>
                                {sectores.map(s => (
                                    <option key={s} value={s}>{formatSectorLabel(s)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-bold">Paciente</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por nombre o RUT..."
                                value={searchFilter}
                                onChange={e => setSearchFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Mes última visita</label>
                            <input
                                type="month"
                                className="form-control"
                                value={mesFilter}
                                onChange={e => setMesFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-outline-secondary w-100"
                                onClick={() => { setSectorFilter(''); setSearchFilter(''); setMesFilter(''); }}
                            >
                                Limpiar filtros
                            </button>
                        </div>
                    </div>
                </div>

                <GdTable
                    columns={columns}
                    data={filtered}
                    defaultItemsPerPage={50}
                    itemsPerPageOptions={[25, 50, 100]}
                    exportFilename="visitas-paciente-coordinacion.xlsx"
                    initialSort={[6, 'asc']}
                    getRowClass={(row) => Number(row.requiere_visita) === 0 ? 'table-warning' : ''}
                    loading={loading}
                />
            </div>
        </>
    );

    if (modal) {
        return (
            <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: "90vw", width: "90vw", maxHeight: "90vh", margin: "5vh auto" }}>
                    <div className="modal-content" style={{ maxHeight: "90vh" }}>
                        {contenido}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-5">
            <div className="card p-4">
                {contenido}
            </div>
        </div>
    );
};

export default VisitasPacienteCoordinacion;
